import React from 'react'
import { css } from '@mxenabled/cssinjs'
import { format } from 'date-fns/format'
import { isSameDay } from 'date-fns/isSameDay'
import { isWithinInterval } from 'date-fns/isWithinInterval'
import { startOfToday } from 'date-fns/startOfToday'

import Button from '@mui/material/Button'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { DATE_SIZE } from './Calendar'

interface DayProps {
  beginDate: Date | undefined
  date: Date
  endDate: Date | undefined
  isInMonth: boolean
  onClick: (date: Date) => void
}

const Day: React.FC<DayProps> = ({ beginDate, date, endDate, isInMonth, onClick }) => {
  const theme = useTheme()
  const styles = getStyles(theme)

  const isInRange =
    beginDate &&
    endDate &&
    isWithinInterval(date, { start: beginDate, end: endDate }) &&
    !isSameDay(date, beginDate) &&
    !isSameDay(date, endDate)

  const isRangeStart =
    beginDate && endDate && isSameDay(beginDate, date) && !isSameDay(beginDate, endDate)

  const isRangeEnd =
    endDate && beginDate && isSameDay(endDate, date) && !isSameDay(beginDate, endDate)

  const isSelected =
    (beginDate && isSameDay(beginDate, date)) || (endDate && isSameDay(endDate, date))

  const isToday = isSameDay(date, startOfToday())

  return (
    <div
      className={`
        mx-cmn-calendar-day
        ${styles}
        ${!isInMonth ? 'mx-cmn-out-of-month' : ''}
        ${isInRange ? 'mx-cmn-date-interval' : ''}
      `}
    >
      <Button
        className={`
          mx-cmn-calendar-day-button
          ${isRangeStart ? 'mx-cmn-range-start' : ''}
          ${isRangeEnd ? 'mx-cmn-range-end' : ''}
        `}
        color={isSelected ? 'primary' : 'secondary'}
        onClick={() => onClick(date)}
        variant={isSelected ? 'contained' : 'text'}
      >
        <Text
          className={`
            mx-cmn-calendar-day-text
            ${isSelected ? 'mx-cmn-day-selected' : ''}
            ${isToday ? 'mx-cmn-today' : ''}`}
          variant="body2"
        >
          {format(date, 'd')}
        </Text>
      </Button>
    </div>
  )
}

const getStyles = (theme: any) =>
  css({
    height: DATE_SIZE,
    width: DATE_SIZE,
    display: 'flex',
    justifyContent: 'center',
    '&.mx-cmn-out-of-month': {
      backgroundColor: theme.palette.background.default,
    },
    '&.mx-cmn-date-interval': {
      backgroundColor: theme.palette.secondary.light,
    },
    '& .MuiButton-root': {
      maxWidth: 44,
      minWidth: 44,
      height: 44,
      padding: 0,
      width: 44,
      '&.mx-cmn-range-start': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        '&:focus': {
          boxShadow: 'none',
        },
      },
      '&.mx-cmn-range-end': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        '&:focus': {
          boxShadow: 'none',
        },
      },
    },
    '& .mx-cmn-calendar-day-text': {
      '&.mx-cmn-day-selected': {
        color: theme.palette.primary,
        '&.mx-cmn-day-selected': {
          borderColor: theme.palette.common.white,
        },
      },
      '&.mx-cmn-today': {
        borderBottom: `solid 2px ${theme.palette.primary.main}`,
        marginBottom: -6,
        paddingBottom: 5,
      },
    },
  })

export default Day
